"use client";

import LogoVector from "@/assets/svg/logo-vector";
import { resolveCettleImage } from "@/cettle/utils";
import { Blog28 } from "@/components/blog28";
import Container from "@/components/container";
import { Testimonial } from "@/components/design-testimonial";
import HeroIndustries from "@/components/hero-industries";
import { NumberTicker } from "@/components/magicui/number-ticker";
import { TextAnimate } from "@/components/magicui/text-animate";
import { PreFooter } from "@/components/preFooter";
import { PresenterMarquee } from "@/components/presenter-marquee";
import { AnimatedBeam } from "@/components/ui/animated-beam";
import DynamicIcon from "@/components/ui/dynamic-icon";
import { MotionPreset } from "@/components/ui/motion-preset";
import { Separator } from "@/components/ui/separator";
import {
  TimelineDotHorizontal,
  TimelineHeadingHorizontal,
  TimelineHorizontal,
  TimelineItemHorizontal,
  TimelineLineHorizontal,
} from "@/components/ui/timeline-horizontal";
import { PageModule } from "@/types/modules";
import type dynamicIconImports from "lucide-react/dynamicIconImports";
import Image from "next/image";
import { useParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { AnimatedBadge } from "@/components/motion/animated-badge";
import { cdn } from "@/lib/cdn";

type DynamicIconName = keyof typeof dynamicIconImports;

interface IndustryNumber {
  value: number;
  suffix?: string;
  label: string;
}

interface IndustryAbout {
  eyebrow: string;
  heading: string;
  overview: string;
  conclusion: string;
}

interface IndustryFeature {
  icon: DynamicIconName;
  title: string;
  description: string;
}

interface IndustryBentoTile {
  kind: "image" | "stat" | "feature" | "callout";
  colSpan: string;
  image?: string;
  overlayHeading?: string;
  overlaySubheading?: string;
  statValue?: number;
  statSuffix?: string;
  statLabel?: string;
  statDescription?: string;
  icon?: DynamicIconName;
  title?: string;
  description?: string;
  bullets?: { icon: DynamicIconName; text: string }[];
}

interface IndustryWorkflowNode {
  icon: DynamicIconName;
  label: string;
}

interface IndustryScenario {
  icon: DynamicIconName;
  eyebrow: string;
  title: string;
  body: string;
  footnote?: string;
}

interface IndustryRecapScene {
  icon: DynamicIconName;
  when: string;
  persona: string;
  body: string;
  checklist?: { icon: DynamicIconName; text: string }[];
}

interface IndustryRecapMoment {
  eyebrow: string;
  heading: string;
  description: string;
  scenes: IndustryRecapScene[];
}

interface IndustryTimelineStep {
  step: string;
  title: string;
  body: string;
}

interface IndustryPersona {
  institution: string;
  quote: string;
  name: string;
  role: string;
}

interface IndustryFaqItem {
  question: string;
  answer: string;
}

interface IndustryContent {
  background: string;
  heading: string;
  subheading: string;
  paragraph: string;
  scrollToExpand: string;
  primaryColor?: string;
  ctaPrimary: { label: string; href: string };
  ctaSecondary: { label: string; href: string };
  phonePage: PageModule;
  about: IndustryAbout;
  numbers: {
    eyebrow: string;
    heading: string;
    items: IndustryNumber[];
  };
  bento: {
    eyebrow: string;
    heading: string;
    description: string;
    tiles: IndustryBentoTile[];
  };
  workflow: {
    eyebrow: string;
    heading: string;
    description: string;
    leftNodes: IndustryWorkflowNode[];
    rightNodes: IndustryWorkflowNode[];
    centerLabel: string;
    features: IndustryFeature[];
  };
  inPractice: {
    eyebrow: string;
    heading: string;
    description: string;
    scenarios: IndustryScenario[];
  };
  recapMoment: IndustryRecapMoment;
  timeline: {
    eyebrow: string;
    heading: string;
    description: string;
    steps: IndustryTimelineStep[];
  };
  personas: {
    eyebrow: string;
    disclosure: string;
    voices: IndustryPersona[];
  };
  faq: {
    eyebrow: string;
    heading: string;
    description: string;
    items: IndustryFaqItem[];
  };
}

interface IndustryContentCollection {
  [key: string]: IndustryContent;
}

const industryContent: IndustryContentCollection = {
  education: {
    background: cdn("industries/education.jpg"),
    heading: "Handyout for Education",
    subheading: "Every lecture deck, in your students' pockets.",
    paragraph:
      "Turn slides into a mobile handout your class can revisit anytime — with feedback, context, and reactions built in.",
    scrollToExpand: "Scroll to explore",
    primaryColor: "#3f6bb8",
    ctaPrimary: { label: "Get started", href: "/auth/signin" },
    ctaSecondary: { label: "See a live demo", href: "/#demo" },
    phonePage: {
      slideId: "industry-education",
      title: "Innovation & Trade Policy",
      modules: [
        {
          id: "edu-intro",
          type: "intro",
          title: "Lecture 4",
          summary:
            "How US-China trade tariffs shifted corporate innovation patterns — a DiD analysis of 705k firm-years.",
          author: {
            firstName: "Emanuel",
            lastName: "Höfling",
            email: "emanuel@handyout.com",
            role: "Advanced Seminar I&E — TUM",
          },
        },
        {
          id: "edu-list",
          type: "list-cards",
          title: "Key takeaways",
          data: {
            cards: [
              {
                icon: "graduation-cap",
                heading: "+31.7%",
                text: "Chinese tariff-exposed firms accelerated innovation post-2018",
              },
              {
                icon: "trending-up",
                heading: "Telecom + Semi",
                text: "Telecom (+17.2%) and Semiconductor (+11.2%) sectors led the surge",
              },
              {
                icon: "target",
                heading: "−9.3%",
                text: "US firms saw a net effect — the tariffs backfired",
              },
            ],
          },
        },
      ],
    },
    about: {
      eyebrow: "About this industry",
      heading: "Lectures your students take with them",
      overview:
        "Turn every lecture deck into a mobile-friendly handyout your students can revisit on their own time. Handyout keeps the slides you presented, adds context, and lets learners take the material with them without printing anything.",
      conclusion:
        "From single classes to multi-week programs, Handyout gives educators a fast way to share polished, interactive handouts — with feedback and engagement built in.",
    },
    numbers: {
      eyebrow: "By the numbers",
      heading: "One QR on screen, a full lecture hall following along",
      items: [
        {
          value: 240,
          label:
            "Students following along on their own device from a single QR scan.",
        },
        {
          value: 12,
          suffix: "wks",
          label:
            "The handout stays live on students' phones through the full term.",
        },
        {
          value: 60,
          suffix: "s",
          label: "From upload to a shareable, mobile-optimised handout.",
        },
      ],
    },
    bento: {
      eyebrow: "In the classroom",
      heading: "What changes when the deck is the handout",
      description:
        "The material students hold on their phone becomes the same material you presented — with the structure, feedback and revisit-value that a static PDF never had.",
      tiles: [
        {
          kind: "image",
          colSpan: "lg:col-span-8 lg:row-span-2 h-[300px] lg:h-full",
          image: cdn("industries/education.jpg"),
          overlayHeading:
            "The lecture that keeps working after the room empties.",
          overlaySubheading:
            "Slides on every phone, revisitable for the full term, with feedback captured while the material is still fresh.",
        },
        {
          kind: "stat",
          colSpan: "lg:col-span-4 min-h-[200px] lg:h-[200px]",
          statValue: 4,
          statLabel: "Feedback dimensions",
          statDescription:
            "Overall, understanding, relevance and actionability — captured before students leave the hall.",
        },
        {
          kind: "feature",
          colSpan: "lg:col-span-4 min-h-[200px] lg:h-[200px]",
          icon: "message-square",
          title: "Comments per slide",
          description:
            "The three questions students never raise out loud — captured attached to the slide that provoked them.",
        },
        {
          kind: "callout",
          colSpan: "lg:col-span-8 min-h-[280px] lg:h-[280px]",
          icon: "book-open",
          title: "Built for how learners actually study",
          description:
            "A lecture handout that lives on a phone is a lecture handout that gets re-read. And re-reading is where comprehension quietly happens.",
          bullets: [
            {
              icon: "check",
              text: "Modules layer on top of the slide — quotes, comparison tables, key takeaways — without replacing the deck you presented.",
            },
            {
              icon: "check",
              text: "Reactions and comments are anchored to the exact slide that raised them, so students can revisit a question days later on the same slide.",
            },
            {
              icon: "check",
              text: "Language auto-detection in analytics tells you which parts of the room actually read the handout, across bilingual and international cohorts.",
            },
          ],
        },
        {
          kind: "feature",
          colSpan: "lg:col-span-4 min-h-[280px] lg:h-[280px]",
          icon: "shield-check",
          title: "Participation without a consent wall",
          description:
            "Students scan, follow along and react without an account, an app or a cookie banner in the way. Analytics are handled transparently and in line with EU data-protection expectations — the room is never the product.",
        },
        {
          kind: "feature",
          colSpan: "lg:col-span-4 min-h-[240px] lg:h-[240px]",
          icon: "languages",
          title: "Language detection",
          description:
            "See which languages the room actually read the handout in — useful for bilingual programmes, joint degrees, and international cohorts where teaching in English does not always mean reading in English.",
        },
        {
          kind: "feature",
          colSpan: "lg:col-span-4 min-h-[240px] lg:h-[240px]",
          icon: "clock",
          title: "Session-based by design",
          description:
            "The handout expires with the semester. No stale course material lingering across cohorts, no forgotten links floating around six months later — sharing has a beginning and a natural end.",
        },
        {
          kind: "feature",
          colSpan: "lg:col-span-4 min-h-[240px] lg:h-[240px]",
          icon: "gauge",
          title: "Structured feedback",
          description:
            "Understanding, relevance and actionability — the three dimensions that tell you not whether the lecture went well, but which half of it needs rewriting before the next cohort.",
        },
      ],
    },
    workflow: {
      eyebrow: "How it fits your teaching",
      heading: "The handout sits between your deck and your students",
      description:
        "Your existing lecture materials stay where they are — PowerPoint, PDF, whatever the department already uses. Handyout takes it from there and adds the layer that turns a slide deck into a handout worth keeping.",
      leftNodes: [
        { icon: "presentation", label: "PowerPoint" },
        { icon: "file-text", label: "PDF" },
      ],
      rightNodes: [
        { icon: "smartphone", label: "Student devices" },
        { icon: "bar-chart-3", label: "Feedback dashboard" },
      ],
      centerLabel: "Handyout",
      features: [
        {
          icon: "upload",
          title: "Direct upload",
          description:
            "Bring the deck you already teach with. PPTX and PDF, straight from your drive — no re-authoring in a proprietary editor, no format war with the department's template.",
        },
        {
          icon: "qr-code",
          title: "One QR per lecture",
          description:
            "Project the code on the opening slide. Students scan, the deck opens on every phone, and the room is following along in seconds — no app to download, no account to create.",
        },
        {
          icon: "gauge",
          title: "Structured feedback",
          description:
            "Understanding, relevance and actionability — captured before students leave, tied to the slides they responded to, ready in the analytics dashboard the moment the lecture ends.",
        },
      ],
    },
    inPractice: {
      eyebrow: "In practice",
      heading: "How a semester with Handyout actually looks",
      description:
        "Three moments where the handout changes what a lecturer knows about their own teaching. None of them require rebuilding the deck; all of them assume the material you already have.",
      scenarios: [
        {
          icon: "sunrise",
          eyebrow: "Day 1 — First lecture",
          title: "Setting the tone before you say a word",
          body:
            "The first time the QR code appears on slide 1, the room hesitates. Is this really instead of the usual printed handout? Show one or two students how to scan, wait fifteen seconds, and watch the phones come out row by row. By slide 3 the first reactions land on the analytics dashboard; by the end of the hour, you know which portion of the cohort actually engaged — and the students already have the handout on the train home.",
          footnote:
            "The setup effort is a two-sentence introduction, once, on day one.",
        },
        {
          icon: "line-chart",
          eyebrow: "Weeks 5–8 — Mid-semester",
          title: "Reading the pattern across five lectures",
          body:
            "Around week five, patterns start to surface across lectures. One slide raises the same question three times — a signal you are leaving a concept underspecified. A module consistently scores lowest on actionability — a hint that the theory has drifted from the seminar practice. The value of mid-semester analytics is not a single engagement number; it is the slide-level pattern across four or five lectures that tells you where the second half needs a different emphasis.",
          footnote:
            "Comments per slide, feedback dimensions, drop-off — read together, not averaged.",
        },
        {
          icon: "book-marked",
          eyebrow: "Weeks 11–12 — Exam preparation",
          title: "The handout as the revision guide",
          body:
            "In the two weeks before the exam, the handout stops being a lecture artefact and starts being a study resource. Students re-open the handout with much longer read-times; delayed comments appear on the trickier slides; the slide with the key takeaways of lecture 4 gets read longer than any other slide. The lecturer sees which slides got the most reading time and can tailor the final review lecture around what the cohort actually did not grasp — instead of what the department assumes will be confusing.",
          footnote:
            "The handout stays live through the exam window and expires cleanly afterwards.",
        },
      ],
    },
    recapMoment: {
      eyebrow: "On the audience side",
      heading: "The handout they actually open again.",
      description:
        "A PPTX on a phone is a pinch-and-zoom nightmare — nobody re-reads it. A handyout is the one artefact your audience opens back up on their own, without prompting, days or weeks after the lecture ends. Three scenes from the other side of the room.",
      scenes: [
        {
          icon: "moon",
          when: "Sunday, 21:47 — exam week",
          persona: "3rd-year student, on the sofa",
          body:
            "The exam is Wednesday. She scans the QR code from a screenshot she took in lecture 4 and lands directly on the slide with the trade-policy chart she vaguely remembers. Two taps expand the module, the comments other students left on that slide give her the framing she missed in the room, and the citation on the slide tells her which paper to look up. Ten minutes, no login, no PDF viewer.",
          checklist: [
            { icon: "target", text: "Jumps to the exact slide she needs" },
            { icon: "message-square", text: "Sees comments other students left" },
            { icon: "book-open", text: "Finds the citation right on the slide" },
          ],
        },
        {
          icon: "briefcase",
          when: "Tuesday, 08:52 — on the way to seminar",
          persona: "TA prepping the discussion section",
          body:
            "Fifteen minutes on the tram before facilitating a section on last week's lecture. He opens the handout on his phone, scrolls through the three modules the lecturer flagged, and re-reads the two takeaways he wants to open the seminar with. The handout is the same material the students hold — same wording, same visualisations — so the section starts on shared ground instead of a paraphrased summary.",
          checklist: [
            { icon: "clock", text: "Full recap in under 15 minutes" },
            { icon: "check", text: "Same content the students have" },
          ],
        },
        {
          icon: "graduation-cap",
          when: "Six weeks later — writing the term paper",
          persona: "Student citing the module",
          body:
            "The term paper needs a source for a claim she remembers from lecture 6. She opens the handout, uses the table of contents to jump straight to that lecture, and copies the citation straight from the slide. What used to be twenty minutes of hunting through a shared drive is thirty seconds on her phone.",
        },
      ],
    },
    timeline: {
      eyebrow: "Across the semester",
      heading: "The four moments that matter, week by week",
      description:
        "Handyout is not a tool you use once and forget. Its value compounds week over week — because the same handout your students opened in week one is the handout they revisit before the exam.",
      steps: [
        {
          step: "Week 1",
          title: "Distribute on scan",
          body:
            "The very first lecture sets the norm. Show the QR, watch the room scan, and the handout is now on the phone of everyone who followed along. From this point, sharing slides is a scan, not an email attachment.",
        },
        {
          step: "Week 4",
          title: "First feedback pattern",
          body:
            "By the fourth lecture, the reactions and structured feedback have enough volume to be readable as a pattern. The slide the room paused on. The lecture where actionability dropped. The two slides that got the most reading time. Not a signal yet, but a texture.",
        },
        {
          step: "Week 8",
          title: "Adjust the second half",
          body:
            "Halfway through the term, the slide-level analytics let you make specific, defensible decisions about the second half of the syllabus. The exercise cohort clearly did not connect with one theoretical model — you rework the linking lecture instead of hoping it lands next time.",
        },
        {
          step: "Weeks 11–12",
          title: "The handout becomes the revision",
          body:
            "In the fortnight before the exam, the handout does the work a lecturer usually has to do in office hours. Delayed comments arrive on the trickier slides; the key takeaways get re-read; the room shows you, in analytics, which portion of the material actually needs a review session.",
        },
      ],
    },
    personas: {
      eyebrow: "Voices from the field",
      disclosure:
        "Composite accounts, drawn from how lecturers in this segment describe their experience. Not testimonials from specific customers.",
      voices: [
        {
          institution: "Advanced seminar, trade policy — composite",
          quote:
            "I used to end every lecture wondering whether the middle third had landed. Now I open the handout on my train ride home — the comments per slide, the four-dimensional feedback, the slide that everyone re-read twice. On Thursday I found out slide 12 raised the same question three times. I will rewrite it before the next cohort.",
          name: "Dr. Halim",
          role: "University lecturer, trade policy",
        },
        {
          institution: "Macroeconomics chair — composite",
          quote:
            "The value of Handyout for me is not the engagement number. It is that I can end a lecture, go home, open the handout, and see the two slides where the room stopped nodding along. Those two slides get twenty minutes of rewriting before the next cohort. That is not a data-driven revolution — it is, for the first time, feedback attached to the thing it is about.",
          name: "Prof. Meier",
          role: "Senior lecturer, macroeconomics",
        },
      ],
    },
    faq: {
      eyebrow: "Common questions",
      heading: "What lecturers ask before they trust the room with a QR",
      description:
        "The five practical questions that come up in every department introduction. Straight answers, no marketing detour.",
      items: [
        {
          question: "Do students need an account or an app?",
          answer:
            "No. A single QR scan opens the handout in the browser, on any phone. No download, no signup, no consent wall — participation is not gated by acquiring the audience as data.",
        },
        {
          question: "How long does the handout stay accessible?",
          answer:
            "Thirty days from the moment you publish, by default. For a term-long course that means each lecture handout is live through the exam period. Publishing again keeps the original expiry date; extend it in the handyout settings.",
        },
        {
          question: "Can I keep the same handout across cohorts?",
          answer:
            "You can re-upload the same deck and publish a fresh handout for each cohort. Analytics stay separated by cohort — so you can see whether the same slide confused Tuesday's group and Thursday's group in the same way, and diagnose whether it is the slide or the audience.",
        },
        {
          question: "What data does Handyout collect from students?",
          answer:
            "The minimum needed to make interaction work. Nothing that identifies individual students unless they choose to write a comment with their name. Analytics are aggregated per slide, not per student — attention, drop-off, and structured feedback totals, not learner profiles.",
        },
        {
          question: "Can co-lecturers see the same feedback dashboard?",
          answer:
            "Yes. Feedback is tied to the handout, and access to the handout can be shared with anyone in your teaching team. There is no per-lecturer paywall on reading the analytics of a handout you co-authored — the dashboard is a team artefact.",
        },
      ],
    },
  },
};

const SECTION_GUTTER = "px-4 lg:px-6 xl:px-0";

const BentoImageTile = ({ tile }: { tile: IndustryBentoTile }) => (
  <div className={`relative overflow-hidden ${tile.colSpan}`}>
    {tile.image && (
      <Image
        src={tile.image}
        unoptimized
        alt={tile.overlayHeading ?? ""}
        fill
        className="object-cover"
        sizes="(min-width: 1024px) 66vw, 100vw"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/75 via-neutral-900/30 to-transparent" />
    <div className="absolute bottom-8 left-8 right-8 z-10 text-white flex flex-col gap-3">
      <h3 className="text-2xl md:text-3xl font-medium leading-tight text-balance">
        {tile.overlayHeading}
      </h3>
      {tile.overlaySubheading && (
        <p className="text-base text-white/85 max-w-md leading-relaxed">
          {tile.overlaySubheading}
        </p>
      )}
    </div>
  </div>
);

const BentoStatTile = ({ tile }: { tile: IndustryBentoTile }) => (
  <div
    className={`border border-neutral-200 bg-white p-6 flex flex-col justify-between ${tile.colSpan}`}
  >
    <div className="flex items-baseline gap-1 text-neutral-900">
      <span className="text-6xl md:text-7xl leading-none font-normal">
        {tile.statValue}
      </span>
      {tile.statSuffix && (
        <span className="text-2xl md:text-3xl leading-none text-neutral-500 font-normal">
          {tile.statSuffix}
        </span>
      )}
    </div>
    <div className="flex flex-col gap-1">
      {tile.statLabel && (
        <p className="text-sm font-medium text-neutral-900">{tile.statLabel}</p>
      )}
      {tile.statDescription && (
        <p className="text-sm text-neutral-500 leading-relaxed">
          {tile.statDescription}
        </p>
      )}
    </div>
  </div>
);

const BentoFeatureTile = ({ tile }: { tile: IndustryBentoTile }) => (
  <div
    className={`border border-neutral-200 bg-white p-6 flex flex-col gap-4 ${tile.colSpan}`}
  >
    {tile.icon && (
      <DynamicIcon name={tile.icon} className="w-5 h-5 text-primary" />
    )}
    <div className="flex flex-col gap-2">
      {tile.title && (
        <h3 className="text-lg font-medium text-neutral-900">{tile.title}</h3>
      )}
      {tile.description && (
        <p className="text-sm text-neutral-500 leading-relaxed">
          {tile.description}
        </p>
      )}
    </div>
  </div>
);

const BentoCalloutTile = ({ tile }: { tile: IndustryBentoTile }) => (
  <div
    className={`border border-neutral-200 bg-neutral-50 p-6 flex flex-col gap-5 ${tile.colSpan}`}
  >
    <div className="flex flex-col gap-3">
      {tile.icon && (
        <DynamicIcon name={tile.icon} className="w-5 h-5 text-primary" />
      )}
      {tile.title && (
        <h3 className="text-lg font-medium text-neutral-900">{tile.title}</h3>
      )}
      {tile.description && (
        <p className="text-sm text-neutral-500 leading-relaxed">
          {tile.description}
        </p>
      )}
    </div>
    {tile.bullets && tile.bullets.length > 0 && (
      <ul className="flex flex-col gap-2.5 mt-auto">
        {tile.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <DynamicIcon
              name={b.icon}
              className="w-4 h-4 text-primary mt-0.5 shrink-0"
            />
            <span className="text-sm text-neutral-700 leading-relaxed">
              {b.text}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const SemesterTimeline = ({
  timeline,
}: {
  timeline: IndustryContent["timeline"];
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = timeline.steps[activeIndex];

  return (
    <div className="mx-auto flex w-full flex-col items-center">
      <TimelineHorizontal
        className="w-full max-w-9/10 flex-row justify-center xl:max-w-none"
        defaultActiveIndex={0}
        animated={true}
        autoPlay={true}
        itemDurations={timeline.steps.map(() => 6000)}
        onActiveIndexChange={setActiveIndex}
      >
        {timeline.steps.map((step, index) => (
          <TimelineItemHorizontal
            key={step.step}
            headingPosition="top"
            className={`items-start gap-4 sm:gap-0 md:max-xl:flex-1 ${
              index !== timeline.steps.length - 1 ? "xl:w-60" : ""
            }`}
          >
            <div className="mb-3 hidden flex-col md:flex">
              <TimelineHeadingHorizontal
                variant="primary"
                className="lg:text-lg"
              >
                {step.step}
              </TimelineHeadingHorizontal>
              <p className="text-muted-foreground">{step.title}</p>
            </div>
            <div className="relative flex w-full items-center">
              <TimelineDotHorizontal
                status="custom"
                className="group [&:not([data-active=true])]:bg-muted [&[data-active=true]]:bg-primary/10 size-4.5 rounded-full"
              >
                <span className="bg-muted-foreground group-data-[active=true]:bg-primary size-3 rounded-full" />
              </TimelineDotHorizontal>
              {index < timeline.steps.length - 1 && (
                <TimelineLineHorizontal className="h-0.5 w-20 sm:w-40 md:w-full" />
              )}
            </div>
          </TimelineItemHorizontal>
        ))}
      </TimelineHorizontal>

      <div className="bg-muted mt-6 flex w-full flex-col items-start gap-4 p-2.5 md:mt-8 lg:mt-12">
        <div className="flex flex-col items-start pl-4 pt-2 gap-1">
          <MotionPreset
            key={`step-${activeIndex}`}
            fade
            blur
            slide={{ direction: "down", offset: 30 }}
            transition={{ duration: 0.5 }}
          >
            <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{active.step}</AnimatedBadge>
          </MotionPreset>
          <MotionPreset
            key={`title-${activeIndex}`}
            fade
            blur
            slide={{ direction: "down", offset: 30 }}
            transition={{ duration: 0.5 }}
            delay={0.15}
          >
            <h3 className="text-foreground text-2xl font-medium">
              {active.title}
            </h3>
          </MotionPreset>
        </div>
        <div className="bg-background w-full p-6 md:p-8">
          <MotionPreset
            key={`body-${activeIndex}`}
            fade
            blur
            slide={{ direction: "down", offset: 30 }}
            transition={{ duration: 0.5 }}
            delay={0.3}
          >
            <p className="text-base text-neutral-600 leading-relaxed">
              {active.body}
            </p>
          </MotionPreset>
        </div>
      </div>
    </div>
  );
};

const WorkflowDiagram = ({
  workflow,
}: {
  workflow: IndustryContent["workflow"];
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const leftRefs = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];
  const rightRefs = [
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
  ];

  return (
    <div
      ref={containerRef}
      className="relative w-full flex flex-col items-center justify-between gap-8 py-16 lg:flex-row lg:gap-0"
    >
      <div className="flex flex-col gap-8 lg:gap-16 z-10">
        {workflow.leftNodes.map((node, i) => (
          <div
            key={node.label}
            ref={leftRefs[i]}
            className="flex items-center gap-3 bg-white border border-neutral-200 px-4 py-3 shadow-sm"
          >
            <DynamicIcon name={node.icon} className="w-5 h-5 text-neutral-700" />
            <span className="text-base font-medium text-neutral-900">
              {node.label}
            </span>
          </div>
        ))}
      </div>

      <div
        ref={centerRef}
        className="z-10 flex items-center gap-3 rounded-full px-5 py-3 bg-primary text-white shadow-2xl"
      >
        <LogoVector className="size-8 text-white shrink-0" />
        <span className="text-xl font-medium">{workflow.centerLabel}</span>
      </div>

      <div className="flex flex-col gap-8 lg:gap-16 z-10">
        {workflow.rightNodes.map((node, i) => (
          <div
            key={node.label}
            ref={rightRefs[i]}
            className="flex items-center gap-3 bg-white border border-neutral-200 px-4 py-3 shadow-sm"
          >
            <DynamicIcon name={node.icon} className="w-5 h-5 text-neutral-700" />
            <span className="text-base font-medium text-neutral-900">
              {node.label}
            </span>
          </div>
        ))}
      </div>

      <AnimatedBeam
        containerRef={containerRef}
        fromRef={leftRefs[0]}
        toRef={centerRef}
        gradientStartColor="var(--primary)"
        duration={4.5}
        className="-z-1 hidden lg:block"
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={leftRefs[1]}
        toRef={centerRef}
        gradientStartColor="var(--primary)"
        duration={4.5}
        className="-z-1 hidden lg:block"
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={centerRef}
        toRef={rightRefs[0]}
        gradientStartColor="var(--primary)"
        duration={4.5}
        className="-z-1 hidden lg:block"
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={centerRef}
        toRef={rightRefs[1]}
        gradientStartColor="var(--primary)"
        duration={4.5}
        className="-z-1 hidden lg:block"
      />
    </div>
  );
};

export function IndustryPage() {
  const params = useParams();
  const id = Array.isArray(params?.id) ? params.id[0] : params?.id;

  const industry = (id && industryContent[id]) || industryContent.education;

  const [useCasePosts, setUseCasePosts] = useState<
    {
      id: string;
      title: string;
      summary: string;
      author: string;
      published: string;
      label: string;
      image: string;
      url: string;
    }[]
  >([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const resetEvent = new Event("resetSection");
    window.dispatchEvent(resetEvent);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/use-cases")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const docs = (data?.docs ?? []) as any[];
        const mapped = docs
          .map((d) => ({
            id: String(d.id ?? d.slug ?? ""),
            title: d.title ?? "",
            summary: d.description ?? "",
            author: d.eyebrow ?? "",
            published: d.publishedAt
              ? new Date(d.publishedAt).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })
              : "",
            label: d.sector ?? "",
            image:
              resolveCettleImage(d.heroImageLeft) ||
              resolveCettleImage(d.heroImageRight),
            url: `/use-cases/${d.slug}`,
          }))
          .filter((p) => p.title && p.image)
          .slice(0, 3);
        setUseCasePosts(mapped);
      })
      .catch(() => {
        if (cancelled) return;
        setUseCasePosts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="flex flex-col w-full font-sans relative">
      <HeroIndustries
        bgImageSrc={industry.background}
        heading={industry.heading}
        subheading={industry.subheading}
        paragraph={industry.paragraph}
        scrollToExpand={industry.scrollToExpand}
        primaryColor={industry.primaryColor}
        ctaPrimary={industry.ctaPrimary}
        ctaSecondary={industry.ctaSecondary}
        phonePage={industry.phonePage}
      />

      <Container containerClassName={SECTION_GUTTER}>
        <div className="flex flex-col gap-4 py-24 max-w-3xl">
          <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{industry.about.eyebrow}</AnimatedBadge>
          <h2 className="text-4xl font-medium text-neutral-900">
            {industry.about.heading}
          </h2>
          <p className="text-lg text-neutral-500 leading-relaxed mt-4">
            {industry.about.overview}
          </p>
          <p className="text-lg text-neutral-500 leading-relaxed">
            {industry.about.conclusion}
          </p>
        </div>
      </Container>

      <Container containerClassName={SECTION_GUTTER}>
        <div className="w-full flex flex-col items-start gap-14 py-24">
          <div className="flex flex-col gap-2 mb-4">
            <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{industry.numbers.eyebrow}</AnimatedBadge>
            <h2 className="text-3xl font-medium text-neutral-900 max-w-3xl lg:text-4xl">
              {industry.numbers.heading}
            </h2>
          </div>
          <div className="grid grid-cols-1 items-start justify-center gap-10 w-full lg:grid-cols-3 lg:gap-16">
            {industry.numbers.items.map((n, idx) => (
              <div
                key={`${n.label}-${idx}`}
                className="flex flex-col gap-4 self-stretch"
              >
                <div className="text-[clamp(6rem,10vw,10rem)] font-normal leading-none text-neutral-900 flex flex-row items-end gap-3">
                  <NumberTicker value={n.value} />
                  {n.suffix && (
                    <TextAnimate animation="fadeIn" by="line" as="span">
                      {n.suffix}
                    </TextAnimate>
                  )}
                </div>
                <h3 className="text-lg font-medium leading-snug text-neutral-700 max-w-sm">
                  {n.label}
                </h3>
              </div>
            ))}
          </div>
          <Separator
            className="bg-neutral-200 w-full"
            orientation="horizontal"
          />
        </div>
      </Container>

      <Container containerClassName={SECTION_GUTTER}>
        <div className="flex flex-col gap-14 py-24">
          <div className="flex flex-col gap-2 max-w-3xl">
            <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{industry.bento.eyebrow}</AnimatedBadge>
            <h2 className="text-4xl font-medium text-neutral-900">
              {industry.bento.heading}
            </h2>
            <p className="text-lg text-neutral-500 leading-relaxed mt-4">
              {industry.bento.description}
            </p>
          </div>
          <div className="grid grid-cols-1 auto-rows-min gap-4 lg:grid-cols-12">
            {industry.bento.tiles.map((tile, idx) => {
              const key = `${tile.kind}-${idx}`;
              if (tile.kind === "image")
                return <BentoImageTile key={key} tile={tile} />;
              if (tile.kind === "stat")
                return <BentoStatTile key={key} tile={tile} />;
              if (tile.kind === "callout")
                return <BentoCalloutTile key={key} tile={tile} />;
              return <BentoFeatureTile key={key} tile={tile} />;
            })}
          </div>
        </div>
      </Container>

      <Container containerClassName={SECTION_GUTTER}>
        <div className="flex flex-col gap-14 py-24">
          <div className="flex flex-col gap-2 max-w-3xl">
            <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{industry.workflow.eyebrow}</AnimatedBadge>
            <h2 className="text-4xl font-medium text-neutral-900">
              {industry.workflow.heading}
            </h2>
            <p className="text-lg text-neutral-500 leading-relaxed mt-4">
              {industry.workflow.description}
            </p>
          </div>
          <WorkflowDiagram workflow={industry.workflow} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {industry.workflow.features.map((feature) => (
              <div
                key={feature.title}
                className="border border-neutral-200 bg-white p-6 flex flex-col gap-4"
              >
                <DynamicIcon
                  name={feature.icon}
                  className="w-5 h-5 text-primary"
                />
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-neutral-900">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-neutral-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>

      <Container containerClassName={SECTION_GUTTER}>
        <div className="flex flex-col gap-14 py-24">
          <div className="flex flex-col gap-2 max-w-3xl">
            <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{industry.inPractice.eyebrow}</AnimatedBadge>
            <h2 className="text-4xl font-medium text-neutral-900">
              {industry.inPractice.heading}
            </h2>
            <p className="text-lg text-neutral-500 leading-relaxed mt-4">
              {industry.inPractice.description}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {industry.inPractice.scenarios.map((s) => (
              <div
                key={s.title}
                className="flex flex-col gap-5 border border-neutral-200 bg-white p-6 lg:p-8"
              >
                <DynamicIcon name={s.icon} className="w-6 h-6 text-primary" />
                <div className="flex flex-col gap-2">
                  <span className="inline-flex h-5 items-center rounded-lg bg-orange-100 px-1.5 font-medium text-[12px] text-orange-700 leading-4 tracking-[-0.12px] w-fit">
                    {s.eyebrow}
                  </span>
                  <h3 className="text-xl font-medium text-neutral-900 leading-snug">
                    {s.title}
                  </h3>
                </div>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {s.body}
                </p>
                {s.footnote && (
                  <p className="text-xs text-neutral-400 italic leading-relaxed mt-auto pt-4 border-t border-neutral-100">
                    {s.footnote}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </Container>

      <Container containerClassName={SECTION_GUTTER}>
        <div className="flex flex-col gap-14 py-24">
          <div className="flex flex-col gap-2 max-w-3xl">
            <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{industry.recapMoment.eyebrow}</AnimatedBadge>
            <h2 className="text-4xl font-medium text-neutral-900">
              {industry.recapMoment.heading}
            </h2>
            <p className="text-lg text-neutral-500 leading-relaxed mt-4">
              {industry.recapMoment.description}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {industry.recapMoment.scenes.map((scene, i) => (
              <div
                key={i}
                className="flex flex-col gap-4 border border-neutral-200 bg-neutral-50 p-6 lg:p-8"
              >
                <div className="flex flex-col gap-1 pb-4 border-b border-neutral-200">
                  <DynamicIcon
                    name={scene.icon}
                    className="w-5 h-5 text-primary mb-2"
                  />
                  <span className="font-mono text-[11px] text-neutral-500">
                    {scene.when}
                  </span>
                  <span className="text-sm font-medium text-neutral-900">
                    {scene.persona}
                  </span>
                </div>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {scene.body}
                </p>
                {scene.checklist && scene.checklist.length > 0 && (
                  <ul className="flex flex-col gap-2 mt-auto pt-4 border-t border-neutral-200">
                    {scene.checklist.map((item, j) => (
                      <li
                        key={j}
                        className="flex flex-row items-center gap-2 text-xs text-neutral-700"
                      >
                        <DynamicIcon
                          name={item.icon}
                          className="w-3.5 h-3.5 text-primary shrink-0"
                        />
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </Container>

      <div className="w-full bg-neutral-50">
        <Container containerClassName={SECTION_GUTTER}>
          <div className="flex flex-col gap-14 py-32">
            <div className="flex flex-col gap-2 max-w-3xl">
              <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{industry.timeline.eyebrow}</AnimatedBadge>
              <h2 className="text-3xl font-medium text-neutral-900 lg:text-4xl">
                {industry.timeline.heading}
              </h2>
              <p className="text-lg text-neutral-500 leading-relaxed mt-4">
                {industry.timeline.description}
              </p>
            </div>
            <SemesterTimeline timeline={industry.timeline} />
          </div>
        </Container>
      </div>

      <Container containerClassName={SECTION_GUTTER}>
        <div className="flex flex-col gap-14 py-24">
          <div className="flex flex-col gap-4 max-w-3xl">
            <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{industry.personas.eyebrow}</AnimatedBadge>
            <p className="text-xs text-neutral-400 italic leading-relaxed max-w-2xl">
              {industry.personas.disclosure}
            </p>
          </div>
          <Testimonial
            testimonials={industry.personas.voices.map((v) => ({
              businessName: v.institution,
              text: v.quote,
              author: { authorName: v.name, title: v.role },
            }))}
          />
        </div>
      </Container>

      <Container containerClassName={SECTION_GUTTER}>
        <Blog28
          tagline="Customer stories"
          heading="Real teams. Real handouts."
          description="See how presenters across SaaS, education, consulting, and pharma ship handouts their audiences actually read."
          buttonText="Browse all use cases"
          buttonUrl="/use-cases"
          posts={useCasePosts}
        />
      </Container>

      <PresenterMarquee />

      <Container containerClassName={SECTION_GUTTER}>
        <div className="flex flex-col gap-14 py-24">
          <div className="flex flex-col gap-2 max-w-3xl">
            <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{industry.faq.eyebrow}</AnimatedBadge>
            <h2 className="text-4xl font-medium text-neutral-900">
              {industry.faq.heading}
            </h2>
            <p className="text-lg text-neutral-500 leading-relaxed mt-4">
              {industry.faq.description}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-x-12 gap-y-6 lg:grid-cols-2 lg:gap-y-8">
            {industry.faq.items.map((item) => (
              <div
                key={item.question}
                className="flex flex-col gap-3 border-t border-neutral-200 pt-6"
              >
                <h3 className="text-lg font-medium text-neutral-900">
                  {item.question}
                </h3>
                <p className="text-base text-neutral-500 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Container>

      <Container className="mb-32">
        <PreFooter />
      </Container>
    </main>
  );
}
